import React, { FunctionComponent, useState } from 'react';
import {
  ButtonBase,
  Card,
  CardContent,
  Chip,
  ChipProps,
  Collapse,
  Divider,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import moment from 'moment';

import { useAppDispatch } from '../../../../../../../../hooks';
import {
  ILessonHistory,
  ILessonHistoryTutorPaymentConcern,
  LESSON_HISTORY_TUTOR_PAYMENT_CONCERN_STATUSES,
  lessonHistoryRaiseConcern,
} from '../../../../../../../../store/V2/LessonHistorySlice';
import { TPTextField } from '../../../../../../../../components/Inputs';

interface TutorPaymentBreakdownTabProps {
  lessonHistory: ILessonHistory;
}

type TState = {
  tutorConcern: string;
  isRaiseConcernInputVisible: boolean;
};

const CustomListItem = ({
  title,
  description,
}: {
  title?: string;
  description?: string;
}) => {
  return (
    <Card
      elevation={0}
      sx={{ backgroundColor: '#F8FAFC', borderRadius: '8px' }}
    >
      <CardContent style={{ padding: '8px 16px' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontSize="16px" fontWeight={500} color="#647488">
            {title}
          </Typography>
          <Typography fontSize="16px" fontWeight={500} color="#1E293B">
            {description}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ConcernStatusCard = ({
  concernData,
}: {
  concernData: ILessonHistoryTutorPaymentConcern;
}) => {
  let chipColor: ChipProps['color'] = 'warning';

  switch (concernData.status) {
    case LESSON_HISTORY_TUTOR_PAYMENT_CONCERN_STATUSES.PENDING:
      chipColor = 'warning';
      break;

    case LESSON_HISTORY_TUTOR_PAYMENT_CONCERN_STATUSES.RESOLVED:
      chipColor = 'success';
      break;

    case LESSON_HISTORY_TUTOR_PAYMENT_CONCERN_STATUSES.REJECTED:
      chipColor = 'error';
      break;

    default:
      chipColor = 'warning';
      break;
  }
  return (
    <Card
      elevation={0}
      sx={{ backgroundColor: '#F8FAFC', borderRadius: '8px' }}
    >
      <CardContent style={{ padding: '16px' }}>
        <Stack gap="8px">
          <Chip
            color={chipColor}
            label={concernData.status.toLowerCase()}
            style={{
              textTransform: 'capitalize',
              padding: '4px 8px',
              height: 'unset',
              width: 'fit-content',
            }}
          />
          <Typography fontSize="16px" fontWeight={500} color="#1E293B">
            {concernData.concern}
          </Typography>
          <Typography fontSize="12px" fontWeight={500} color="#1E293B">
            {moment(concernData.createdAt).format('DD MMM YYYY, hh:mm A')}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

const AdminRemarksCard = ({
  concernData,
}: {
  concernData: ILessonHistoryTutorPaymentConcern;
}) => {
  return (
    <Card
      elevation={0}
      sx={{ backgroundColor: '#F8FAFC', borderRadius: '8px' }}
    >
      <CardContent style={{ padding: '16px' }}>
        <Stack gap="8px">
          <Typography fontSize="16px" fontWeight={600} color="#1E293B">
            Admin Remarks
          </Typography>
          <Typography fontSize="16px" fontWeight={500} color="#1E293B">
            {concernData.adminNote}
          </Typography>
          {concernData.respondedAt ? (
            <Typography fontSize="12px" fontWeight={500} color="#1E293B">
              {moment(concernData.respondedAt).format('DD MMM YYYY, hh:mm A')}
            </Typography>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};

const initialState: TState = {
  tutorConcern: '',
  isRaiseConcernInputVisible: false,
};

const TutorPaymentBreakdownTab: FunctionComponent<TutorPaymentBreakdownTabProps> = ({
  lessonHistory,
}) => {
  const dispatch = useAppDispatch();

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const submitConcern = async () => {
    setLoading(true);

    await dispatch(
      lessonHistoryRaiseConcern({
        lessonHistoryId: lessonHistory._id,
        concern: state.tutorConcern,
      })
    );

    setLoading(false);

    updateStateAttributes({
      isRaiseConcernInputVisible: false,
      tutorConcern: '',
    });
  };

  const renderContent = () => {
    if (lessonHistory.tutorRate) {
      const tutorRate = (lessonHistory.tutorRate || 0).toFixed(2);
      const tutorAmount = (lessonHistory.tutorAmount || 0).toFixed(2);
      const allocatedTutorAmount = (
        lessonHistory.allocatedTutorAmount || 0
      ).toFixed(2);

      const lessonExtensionTutorAmount = (
        lessonHistory.lessonExtensionTutorAmount || 0
      ).toFixed(2);

      const tutorPenaltyAmount = (
        lessonHistory.tutorPenaltyAmount || 0
      ).toFixed(2);

      return (
        <div>
          <div>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CustomListItem
                  title="Tutor Rate / Hour:"
                  description={`${
                    lessonHistory.tutorRateCurrency || ''
                  } ${tutorRate}`.trim()}
                />
              </Grid>
              <Grid item xs={12}>
                <CustomListItem
                  title="Class Duration"
                  description={`${lessonHistory.duration || 0} minutes`}
                />
              </Grid>
              <Grid item xs={12}>
                <CustomListItem
                  title="+ Base Rate"
                  description={`${
                    lessonHistory.tutorAmountCurrency || ''
                  } ${allocatedTutorAmount}`.trim()}
                />
              </Grid>
              {lessonHistory.lessonExtension &&
              lessonHistory.lessonExtension >= 15 ? (
                <Grid item xs={12}>
                  <div style={{ minHeight: '15px' }} />
                  <CustomListItem
                    title="Extended Lesson Duration"
                    description={`${lessonHistory.lessonExtension} minutes`}
                  />
                </Grid>
              ) : null}
              {lessonHistory.lessonExtension &&
              lessonHistory.lessonExtension >= 15 &&
              lessonHistory.lessonExtensionTutorAmount ? (
                <Grid item xs={12}>
                  <CustomListItem
                    title="+ Extended Lesson Rate"
                    description={`${
                      lessonHistory.tutorAmountCurrency || ''
                    } ${lessonExtensionTutorAmount}`.trim()}
                  />
                </Grid>
              ) : null}
              {lessonHistory.tutorPenalty ? (
                <Grid item xs={12}>
                  <div style={{ minHeight: '15px' }} />
                  <CustomListItem
                    title={`- Penalty ${parseFloat(
                      lessonHistory.tutorPenalty.toFixed(2)
                    ).toString()}%`}
                    description={`${
                      lessonHistory.tutorAmountCurrency || ''
                    } ${tutorPenaltyAmount}`.trim()}
                  />
                </Grid>
              ) : null}
              {lessonHistory.tutorPenalty &&
              lessonHistory.tutorPenaltyReason ? (
                <Grid item xs={12}>
                  <CustomListItem
                    title="Penalty Reason"
                    description={lessonHistory.tutorPenaltyReason}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <div style={{ minHeight: '15px' }} />
                <CustomListItem
                  title="Total"
                  description={`${
                    lessonHistory.tutorAmountCurrency || ''
                  } ${tutorAmount}`.trim()}
                />
              </Grid>
            </Grid>
          </div>
          <div style={{ padding: '16px 0' }}>
            <Divider />
          </div>
          <div>
            <div>
              <Typography fontWeight={600} fontSize="16px">
                Raise Concern
              </Typography>
            </div>
            <div style={{ paddingTop: '6px' }}>
              <Typography fontSize="14px" fontWeight={500} color="#647488">
                Resolve payment concerns with ease. This modal is your direct
                route to addressing any payment issues as a tutor.
              </Typography>
            </div>
            {!lessonHistory.tutorPaymentConcern ? (
              <div style={{ paddingTop: '6px' }}>
                <ButtonBase
                  sx={{ textDecoration: 'underline', fontWeight: 600 }}
                  onClick={() =>
                    updateStateAttributes({
                      isRaiseConcernInputVisible: !state.isRaiseConcernInputVisible,
                    })
                  }
                >
                  Raise a concern?
                </ButtonBase>
              </div>
            ) : null}
            {lessonHistory.tutorPaymentConcern ? (
              <div style={{ paddingTop: '16px' }}>
                <Stack gap="16px">
                  <ConcernStatusCard
                    concernData={lessonHistory.tutorPaymentConcern}
                  />
                  {lessonHistory.tutorPaymentConcern.adminNote ? (
                    <AdminRemarksCard
                      concernData={lessonHistory.tutorPaymentConcern}
                    />
                  ) : null}
                </Stack>
              </div>
            ) : null}
            <Collapse in={state.isRaiseConcernInputVisible}>
              <div style={{ paddingTop: '16px' }}>
                <TPTextField
                  fullWidth
                  multiline
                  rows={3}
                  disabled={isLoading}
                  value={state.tutorConcern}
                  placeholder="Type your concern here..."
                  onChange={(event) =>
                    updateStateAttributes({ tutorConcern: event.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={submitConcern}
                          disabled={
                            !state.tutorConcern.trim().length || isLoading
                          }
                        >
                          <SendIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onKeyDown={(event) => {
                    if (
                      event.keyCode === 13 &&
                      state.tutorConcern.trim().length
                    ) {
                      submitConcern();
                    }
                  }}
                />
              </div>
            </Collapse>
          </div>
        </div>
      );
    }

    return (
      <>
        <Typography variant="h6">
          No Tutor Payment Details are available.
        </Typography>
      </>
    );
  };
  return (
    <Fade in>
      <div>
        <div style={{ marginBottom: '16px' }}>
          <Typography fontWeight={600} fontSize="16px">
            Total Summary
          </Typography>
        </div>
        <div>{renderContent()}</div>
      </div>
    </Fade>
  );
};

export default TutorPaymentBreakdownTab;
